<template>
  <div>
    <!-- Navigation Bar -->
    <nav class="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/10 border-b border-white/20 supports-[backdrop-filter]:bg-white/10">
      <div class="container mx-auto px-8 h-24">
        <div class="flex justify-between items-center h-full">
          <!-- Logo -->
          <div class="flex items-center space-x-3">
            <img class="w-16" src="https://toolbox.state.rs/img/statestoolbox.png" alt="">
            <span class="text-2xl font-bold text-white tracking-tight">State Lightbox</span>
          </div>

          <!-- Search Bar (Center) -->
          <div class="hidden lg:flex items-center flex-1 max-w-xl mx-8">
            <div class="relative w-full">
              <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg class="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input 
                type="search" 
                v-model="searchQuery"
                class="w-full bg-white/10 backdrop-blur-xl border border-white/20 text-white rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-white/50" 
                placeholder="Suche nach Gesetz..."
              >
            </div>
          </div>

          <!-- Right Side: Version & Online Count -->
          <div class="flex items-center space-x-6">
            <!-- Version Number -->
            <div class="hidden md:flex items-center space-x-2 text-white/70">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span class="text-sm font-medium">v08.06.2025:1300</span>
            </div>
            
            <!-- Online Count -->
            <div class="hidden md:flex items-center space-x-2 text-white/70">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
              <span class="text-sm text-green-500 font-medium">{{visitorCount}} online</span> 
            </div>
            <div class="hidden md:flex items-center space-x-2 text-white/30">
              <span class="text-sm font-medium">powered by Nathan Shadowfox</span>
            </div>
            <!-- Mobile Menu Button -->
            <button class="lg:hidden text-white/70" @click="toggleMobileMenu">
              <svg class="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div class="lg:hidden border-t border-white/20" :class="{ 'hidden': !mobileMenuOpen }">
        <div class="container mx-auto px-8 py-8 space-y-6 backdrop-blur-xl bg-white/10">
          <!-- Mobile Search -->
          <div class="relative">
            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg class="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <input 
              type="search" 
              v-model="searchQuery"
              class="w-full bg-white/10 backdrop-blur-xl border border-white/20 text-white rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-white/50" 
              placeholder="Suche nach Gesetz..."
            >
          </div>
          
          <!-- Mobile Version & Online Count -->
          <div class="flex justify-between">
            <div class="flex items-center space-x-2 text-white/70">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span class="text-sm font-medium">v05.04.2025:1400</span>
            </div>
            <div class="flex items-center space-x-2 text-white/70">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
              <span class="text-sm text-green-500 font-medium">{{visitorCount}} online</span> 
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Spacer for the fixed navbar -->
    <div class="h-24"></div>

    <div class="w-full p-4">
      <!-- Main content area with flexible full-width layout -->
      <div class="w-full flex flex-col lg:flex-row mt-4">
        <!-- Left side: Law categories and table -->
        <div class="w-full lg:w-2/3 pr-0 lg:pr-6">
          <!-- Law Categories Navigation -->
          <div class="flex flex-wrap gap-4 mb-8 overflow-x-auto pb-2">
            <button 
              v-for="category in categories" 
              :key="category.shortener" 
              @click="activeCategory = category.shortener"
              :class="[
                'px-6 py-3 rounded-xl text-white font-medium transition-all backdrop-blur-xl shadow-lg whitespace-nowrap',
                activeCategory === category.shortener 
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500 shadow-indigo-500/30' 
                  : 'bg-white/10 hover:bg-white/20'
              ]"
            >
              {{ category.topic }} ({{ category.shortener }})
            </button>
          </div>

          <!-- Laws Table with scrollable container -->
          <div class="rounded-xl backdrop-blur-xl bg-white/10 border border-white/20 shadow-xl overflow-hidden max-h-[calc(100vh-220px)] overflow-y-auto custom-scrollbar">
            <!-- Fixed Table Header -->
            <div class="sticky top-0 z-10 grid grid-cols-12 gap-2 px-6 py-4 border-b border-white/20 bg-white/10 text-white font-medium backdrop-blur-xl">
              <div class="col-span-1">§</div>
              <div class="col-span-5">Gesetz</div>
              <div class="col-span-2">Strafe</div>
              <div class="col-span-2">Wanteds</div>
              <div class="col-span-2">Info</div>
            </div>

            <!-- Table Body with Sections -->
            <div class="divide-y divide-white/10">
              <template v-for="(category, catIndex) in filteredCategories" :key="catIndex">
                <!-- Section Heading (sticky within scroll container) -->
                <div class="sticky top-14 z-10 px-6 py-3 bg-white/10 backdrop-blur-xl text-white font-medium flex items-center space-x-3">
                  <span class="text-lg">{{ category.topic }}</span>
                  <a :href="category.lawUrl" target="_blank" class="text-indigo-300 hover:text-indigo-200 text-sm">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                    </svg>
                    Details
                  </a>
                </div>

                <!-- Laws -->
                <div>
                  <div 
                    v-for="law in filterLaws(category.paragraphs)" 
                    :key="law.id" 
                    @click="selectLaw(law.id)"
                    :class="[
                      'grid grid-cols-12 gap-2 px-6 py-4 text-white transition-all cursor-pointer hover:bg-white/10',
                      selectedLaws.includes(law.id) ? 'bg-indigo-500/20 border-l-4 border-indigo-500' : ''
                    ]"
                  >
                    <div class="col-span-1 font-medium">{{ law.paragraph }}</div>
                    <div class="col-span-5">{{ law.law }}</div>
                    <div class="col-span-2">{{ formatCurrency(law.fine) }}</div>
                    <div class="col-span-2">{{ law.displayWanted }}</div>
                    <div class="col-span-2 flex items-center">
                      <span v-if="law.info" class="px-2 py-1 rounded-lg bg-white/10 text-xs">
                        {{ law.info }}
                      </span>
                      <span v-if="law.fib" class="ml-2 px-2 py-1 rounded-lg bg-blue-500/30 text-xs mr-1">
                        FIB
                      </span>
                      <span v-if="law.sta" class="ml-2 px-2 py-1 rounded-lg bg-purple-500/30 text-xs">
                        STA
                      </span>
                    </div>
                  </div>
                </div>
              </template>
              
              <!-- No results message -->
              <div v-if="filteredCategories.length === 0 || allFilteredLawsEmpty" class="text-white/50 text-center py-8">
                Keine Gesetze gefunden für "{{ searchQuery }}"
              </div>
            </div>
          </div>
        </div>

        <!-- Right side: Sticky details and action buttons -->
        <div class="w-full lg:w-1/3 mt-6 lg:mt-0 lg:sticky lg:top-4 lg:self-start">
          <!-- Action buttons and tabs -->
          <div class="mb-4">
            <div class="flex justify-between mb-3">
              <button 
                @click="newAct()" 
                class="px-6 py-3 text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl hover:opacity-90 transition-all shadow-lg shadow-blue-500/20">
                NEUE AKTE
              </button>
              
              <button 
                @click="setTimeAndDateNow()" 
                class="px-6 py-3 text-white bg-white/10 backdrop-blur-xl rounded-xl hover:bg-white/20 transition-all">
                AKTUELLE ZEIT
              </button>
            </div>
            
            <!-- Tab navigation -->
            <div class="flex rounded-t-xl overflow-hidden">
              <button 
                @click="activeTab = 'details'" 
                class="flex-1 py-3 text-white transition-all"
                :class="activeTab === 'details' ? 'bg-white/20 font-medium' : 'bg-white/10 hover:bg-white/15'"
              >
                Täterdetails
              </button>
              <button 
                @click="activeTab = 'crimes'" 
                class="flex-1 py-3 text-white transition-all"
                :class="activeTab === 'crimes' ? 'bg-white/20 font-medium' : 'bg-white/10 hover:bg-white/15'"
              >
                Straftaten <span v-if="selectedLaws.length" class="ml-1 px-2 py-0.5 rounded-full bg-indigo-500/50 text-xs">{{ selectedLaws.length }}</span>
              </button>
            </div>
          </div>

          <!-- Selected Laws Summary -->
          <div class="backdrop-blur-xl bg-white/10 border border-white/20 shadow-xl rounded-xl mb-4 p-4">
            <div class="flex justify-between items-center mb-2">
              <h3 class="text-lg font-bold text-white">Zusammenfassung</h3>
              <div class="text-white">
                <span 
                  @click="copyClipboard(totalFine)" 
                  class="font-medium text-green-500 cursor-pointer"
                >{{ formatCurrency(totalFine) }}</span> | 
                <span
                  @click="copyClipboard(totalWanteds)"
                  class="text-amber-400 cursor-pointer"
                >{{ totalWanteds }}</span>
              </div>
            </div>
            
            <div v-if="totalWanteds > 0" class="text-white/70 text-center font-medium mb-2">
              <strong class="cursor-pointer" @click="prisonerData.name" v-if="prisonerData.name && prisonerData.name.length > 0">{{ prisonerData.name }}</strong> <em class="cursor-pointer text-white/30" @click="prisonerData.licensePlate" v-if="prisonerData.licensePlate && prisonerData.licensePlate.length > 0">({{ prisonerData.licensePlate }})</em>
            </div>
            <!-- Arrest time -->
            <div v-if="totalWanteds > 0" class="text-red-500 text-center font-medium mb-2">
              INHAFTIERUNG ZEIT: <strong>{{ arrestTime }}</strong>
            </div>
            

            <!-- All selected paragraphs visual overview -->
            <div v-if="selectedLaws.length > 0" class="mb-3">
              <p class="text-white/70 text-sm mb-2">Ausgewählte Paragraphen:</p>
              <div class="flex flex-wrap gap-2">
                <template v-for="group in allSelectedParagraphsGrouped" :key="group.shortener">
                  <span 
                    v-for="law in group.laws" 
                    :key="law.paragraph + group.shortener"
                    :class="[
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                      law.isLeading 
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/30 ring-2 ring-indigo-400/50' 
                        : 'bg-white/10 text-white/60'
                    ]"
                    :title="law.isLeading ? 'Führende Straftat – wird für Bußgeld/Wanteds verwendet' : law.law"
                  >
                    {{ law.paragraph }} {{ group.shortener }}
                    <span v-if="law.isLeading" class="ml-1">★</span>
                  </span>
                </template>
              </div>
              <p v-if="selectedLaws.length > 1" class="text-white/40 text-xs mt-1">★ = Führende Straftat (höchstes Bußgeld/Wanteds)</p>
            </div>

            <!-- Requirements badges -->
            <div v-if="selectedLaws.length > 0" class="flex justify-center gap-4 mb-3">
              <div v-if="needsFib && needsSta" class="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium">
                FIB & STA benötigt
              </div>
              <div v-else-if="needsFib" class="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium">
                FIB benötigt
              </div>
              <div v-else-if="needsSta" class="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium">
                STA benötigt
              </div>
              <div v-else class="bg-green-600 text-white px-4 py-2 rounded-lg font-medium">
                Kein FIB und STA erforderlich
              </div>
            </div>

            <!-- PDA Fine Text -->
            <div v-if="selectedLaws.length > 0" class="mb-3">
              <p class="text-white/70 text-sm mb-1">PDA Bußgeld Text:</p>
              <div 
                @click="copyClipboard(generatePdaFormat())" 
                class="bg-black/30 rounded-lg p-2 text-white text-sm cursor-pointer hover:bg-black/40"
              >
                {{ generatePdaFormat() }}
              </div>
            </div>
            
            <!-- PDA Wanted Text -->
            <div v-if="totalWanteds > 0" class="mb-3">
              <p class="text-white/70 text-sm mb-1">PDA Wanted Text:</p>
              <div 
                @click="copyClipboard(generatePdaWantedFormat())" 
                class="bg-black/30 rounded-lg p-2 text-white text-sm cursor-pointer hover:bg-black/40"
              >
                {{ generatePdaWantedFormat() }}
              </div>
            </div>
            
            <div v-if="needsFineSplitting" class="mb-3">
  <p class="text-white/70 text-sm mb-1">Aufgeteilte Bußgelder:</p>
  <div 
    v-for="(split, index) in generateFineSplits()" 
    :key="index"
    class="bg-black/30 rounded-lg mb-2 overflow-hidden"
  >
    <div class="flex justify-between items-center">
      <div 
        @click="copyClipboard(split.text)" 
        class="p-2 text-white text-sm cursor-pointer hover:bg-black/40 flex-grow"
      >
        {{ split.text }}
      </div>
      <div @click="copyClipboard(split.amount)" class="cursor-pointer  bg-green-600/30 p-2 text-green-400 font-medium">
        {{ split.amount }}
      </div>
    </div>
  </div>
</div>
            
            <!-- Ersatzfreiheitsstrafe option -->
            <div class="flex items-center mt-2">
              <input 
                type="checkbox" 
                id="ersatzfreiheitsstrafe" 
                v-model="ersatzfreiheitsstrafe"
                class="w-4 h-4 rounded border-white/30 bg-white/10 text-indigo-500 focus:ring-indigo-500"
              >
              <label for="ersatzfreiheitsstrafe" class="ml-2 text-white">
                {{ !ersatzfreiheitsstrafe ? "Keine Ersatzfreiheitsstrafe (Standard)" : "Ersatzfreiheitsstrafe" }}
              </label>
            </div>
            
            <!-- Add issue time option -->
            <div class="flex items-center mt-2">
              <input 
                type="checkbox" 
                id="addIssueTime" 
                v-model="addIssueTime"
                class="w-4 h-4 rounded border-white/30 bg-white/10 text-indigo-500 focus:ring-indigo-500"
              >
              <label for="addIssueTime" class="ml-2 text-white">
                Ausstellungszeit hinzufügen
              </label>
            </div>
            
            <!-- Clear selection button -->
            <button 
              v-if="selectedLaws.length > 0"
              @click="clearSelection" 
              class="px-4 py-2 text-sm text-white bg-red-500/20 hover:bg-red-500/40 rounded-lg transition-all w-full mt-3"
            >
              Auswahl löschen
            </button>
          </div>

          <!-- Tabbed content area -->
          <div class="backdrop-blur-xl bg-white/10 border border-white/20 shadow-xl rounded-xl overflow-hidden">
            <!-- Prisoner info form -->
            <div v-if="activeTab === 'details'">
              <div class="p-4 border-b border-white/20 bg-white/5">
                <h3 class="text-white font-medium">Täterdetails</h3>
              </div>
              
              <div class="p-4">
                <div class="grid grid-cols-2 gap-4">
                  <div class="col-span-2 md:col-span-1">
                    <label class="block text-white mb-2 text-sm">Datum</label>
                    <input 
                      type="date" 
                      v-model="prisonerData.day" 
                      class="w-full bg-black/30 border border-white/20 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                  </div>
                  
                  <div class="col-span-2 md:col-span-1">
                    <label class="block text-white mb-2 text-sm">Uhrzeit/Handschellen <span class="text-red-500">*</span></label>
                    <input 
                      type="time" 
                      v-model="prisonerData.time" 
                      class="w-full bg-black/30 border border-white/20 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-amber-400"
                      @change="countTimeForArrest()"
                    >
                  </div>
                  
                  <div class="col-span-2">
                    <label class="block text-white mb-2 text-sm">PDA Zusatzinfo (Optional)</label>
                    <input 
                      type="text" 
                      v-model="prisonerData.pdaNotes" 
                      placeholder="Bsp. STA,...+1 Wanted, Ausbildung, ist ungültig,..."
                      class="w-full bg-black/30 border border-white/20 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                  </div>
                  
                  <div class="col-span-2 md:col-span-1">
                    <label class="block text-white mb-2 text-sm">Name <span class="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      v-model="prisonerData.name" 
                      placeholder="John Doe" 
                      class="w-full bg-black/30 border border-white/20 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                  </div>
                  
                  <div class="col-span-2 md:col-span-1">
                    <label class="block text-white mb-2 text-sm">KFZ Kennzeichen (Optional)</label>
                    <input 
                      type="text" 
                      v-model="prisonerData.licensePlate" 
                      placeholder="1HWP230"
                      class="w-full bg-black/30 border border-indigo-500/50 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                  </div>
                  
                  <div class="col-span-2">
                    <label class="block text-white mb-2 text-sm">Ort (Optional)</label>
                    <input 
                      type="text" 
                      v-model="prisonerData.location" 
                      placeholder="Ort" 
                      class="w-full bg-black/30 border border-white/20 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                  </div>
                  
                  <div v-if="totalWanteds > 0" class="col-span-2">
                    <label class="block text-white mb-2 text-sm">Wanted Reduzierung gemäß §9 StPO</label>
                    <select 
                      v-model="wantedReduce" 
                      class="w-full bg-black/30 border border-white/20 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="0">0</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </div>
                  
                  <div class="col-span-2">
                    <button 
                      @click="saveRecord()" 
                      class="w-full px-4 py-3 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl hover:opacity-90 transition-all shadow-lg shadow-indigo-500/20 mt-2"
                    >
                      AKTE ERSTELLEN
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Selected Laws List with its own scroll area -->
            <div v-if="activeTab === 'crimes'">
              <div class="p-4 border-b border-white/20 bg-white/5">
                <h3 class="text-white font-medium">Straftaten</h3>
              </div>
              
              <!-- Scrollable selected laws list -->
              <div class="max-h-[calc(100vh-350px)] overflow-y-auto p-4 space-y-3 custom-scrollbar">
                <div 
                  v-for="law in selectedLawsDetails" 
                  :key="law.id"
                  class="py-3 px-4 bg-black/30 rounded-lg relative"
                >
                  <div class="pr-8">
                    <div class="flex justify-between">
                      <div class="font-medium text-white">{{ law.paragraph }}</div>
                      <div class="text-amber-400">{{ law.displayWanted }}</div>
                    </div>
                    <div class="text-white mt-1">{{ law.law }}</div>
                    <div v-if="law.info" class="text-blue-400 text-sm mt-1 italic">{{ law.info }}</div>
                    <div 
                      @click="copyClipboard(formatCurrency(law.fine))" 
                      class="text-green-500 font-medium mt-2 cursor-pointer"
                    >
                      {{ formatCurrency(law.fine) }}
                    </div>
                    <div v-if="law.exclusion_info" class="text-red-400 text-xs mt-1">
                      Schließt {{ law.exclusion_info }} aus
                    </div>
                    <div class="flex mt-2 gap-2">
                      <span v-if="law.fib" class="px-2 py-1 rounded-lg bg-blue-500/30 text-xs">
                        FIB
                      </span>
                      <span v-if="law.sta" class="px-2 py-1 rounded-lg bg-purple-500/30 text-xs">
                        STA
                      </span>
                    </div>
                  </div>
                  
                  <!-- Remove button -->
                  <button 
                    @click.stop="removeLaw(law.id)" 
                    class="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full bg-red-500/80 hover:bg-red-500 text-white"
                  >
                    ✕
                  </button>
                </div>
                
                <div v-if="selectedLaws.length === 0" class="text-white/50 text-center py-8">
                  Keine Gesetze ausgewählt
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import io from 'socket.io-client';

export default {
  data() {
    return {
      visitorCount: 0,
      socket: null,
      lawData: {
        topGesetze: [],
        fibGesetze: [],
        getMaximumFineVar: 150000,
        gesetze: []
      },
      activeCategory: 'StGB', // Default active category
      activeTab: 'details',    // Default active tab
      selectedLaws: [],       // Array of selected law IDs
      prisonerData: {
        name: "",
        licensePlate: "",
        location: "",
        day: "",
        time: "",
        pdaNotes: ""
      },
      ersatzfreiheitsstrafe: false,
      addIssueTime: false,
      wantedReduce: "0",
      arrestTime: "",
      arrestCountdown: null,
      searchQuery: "",    // For search function
      mobileMenuOpen: false, // For mobile menu
      maxEntryFine: 50000  // Maximum fine amount that can be entered in one go
    }
  },
  computed: {
    categories() {
      return this.lawData.gesetze;
    },
    filteredCategories() {
      if (!this.searchQuery) {
        // If no search query, apply regular category filter
        if (this.activeCategory === 'all') {
          return this.categories;
        }
        return this.categories.filter(cat => cat.shortener === this.activeCategory);
      } else {
        // With search query, show all categories but filter paragraphs
        return this.categories.map(category => {
          // Create a copy of the category with filtered paragraphs
          return {
            ...category,
            paragraphs: category.paragraphs.filter(law => this.lawMatchesSearch(law))
          };
        }).filter(category => category.paragraphs.length > 0); // Only keep categories with matching laws
      }
    },
    allFilteredLawsEmpty() {
      if (this.filteredCategories.length === 0) return true;
      
      // Check if all categories have empty paragraphs arrays
      return this.filteredCategories.every(category => category.paragraphs.length === 0);
    },
    selectedLawsDetails() {
      return this.selectedLaws.map(id => {
        for (const category of this.lawData.gesetze) {
          const law = category.paragraphs.find(p => p.id === id);
          if (law) {
            // Add the shortener to the law object for reference
            return {...law, shortener: category.shortener};
          }
        }
        return null;
      }).filter(Boolean);
    },
    // Führende Straftat: Sortierung nach Wanted DESC, dann Bußgeld DESC (Tiebreaker)
    leadingCase() {
      if (this.selectedLawsDetails.length === 0) return null;

      const sorted = [...this.selectedLawsDetails].sort((a, b) => {
        const wantedDiff = parseInt(b.wanteds || 0) - parseInt(a.wanteds || 0);
        if (wantedDiff !== 0) return wantedDiff;
        return parseFloat(b.fine) - parseFloat(a.fine);
      });

      return sorted[0];
    },
    // Bußgeld basierend auf führender Straftat (Entry[0])
    totalFine() {
      const leading = this.leadingCase;
      if (!leading) return 0;

      let maxFine = parseFloat(leading.fine);
      return maxFine > this.lawData.getMaximumFineVar ? this.lawData.getMaximumFineVar : maxFine;
    },
    rawWanteds() {
      const leading = this.leadingCase;
      return leading ? parseInt(leading.wanteds || 0) : 0;
    },
    // Wanteds basierend auf führender Straftat (Entry[0])
    totalWanteds() {
      let wanteds = this.rawWanteds;

      // Add additional wanteds for Ersatzfreiheitsstrafe
      if (this.ersatzfreiheitsstrafe) {
        wanteds += 1 + Math.trunc(this.totalFine / 25001);
      }

      // Apply wanted reduction
      wanteds -= parseInt(this.wantedReduce);

      // Ensure wanteds are between 0 and 5
      return Math.min(Math.max(wanteds, 0), 5);
    },
    // Vermerk bei Lizenzentzug: WaffG-Paragraphen mit Konfiszierung/Entzug in info
    licenseRevocationNote() {
      // Find selected WaffG laws whose info contains "Konfiszieren" or "Entzug"
      const waffgLaws = this.selectedLawsDetails.filter(law => 
        law.shortener === 'WaffG' && 
        law.info && 
        (law.info.toLowerCase().includes('konfiszieren') || law.info.toLowerCase().includes('entzug'))
      );
      
      if (waffgLaws.length === 0) return "";
      
      // Return all matching WaffG paragraphs as notes, e.g. "(WaffG §5.1)"
      // If multiple, join them: "(WaffG §5.1, §5.3)"
      const paragraphs = waffgLaws.map(l => l.paragraph);
      return `(WaffG ${paragraphs.join(', ')})`;
    },
    returnAllParagraphs() {
      // Geldstrafen-Anzeige:
      // - StGB (und andere): nur die schwerwiegendste Einzelstraftat
      // - WaffG & StVO: alle Verstöße unabhängig hinten angehängt
      // Format: "StGB §25, WaffG §5.1, StVO §6f"
      if (this.selectedLawsDetails.length === 0) return "";

      const grouped = {};
      for (const law of this.selectedLawsDetails) {
        const sh = law.shortener;
        if (!grouped[sh]) grouped[sh] = [];
        grouped[sh].push(law);
      }

      const parts = [];
      for (const cat of this.categories) {
        const sh = cat.shortener;
        if (!grouped[sh]) continue;

        if (sh === 'WaffG' || sh === 'StVO') {
          const paragraphs = grouped[sh].map(l => l.paragraph).join(' ');
          parts.push(`${sh} ${paragraphs}`);
        } else {
          const leading = [...grouped[sh]].sort((a, b) => {
            const wDiff = parseInt(b.wanteds || 0) - parseInt(a.wanteds || 0);
            if (wDiff !== 0) return wDiff;
            return parseFloat(b.fine) - parseFloat(a.fine);
          })[0];
          parts.push(`${sh} ${leading.paragraph}`);
        }
      }

      return parts.join(', ');
    },
    returnAllParagraphsWanteds() {
      // Show only the paragraph of the leading case (highest wanteds/fine)
      const leading = this.leadingCase;
      if (!leading || parseInt(leading.wanteds || 0) <= 0) return "";
      
      let text = `${leading.shortener} ${leading.paragraph}`;
      
      // Append license revocation note if applicable
      if (this.licenseRevocationNote && !(leading.shortener === 'WaffG' && this.licenseRevocationNote.includes(leading.paragraph))) {
        text += ` ${this.licenseRevocationNote}`;
      }
      
      return text;
    },
    allSelectedParagraphsGrouped() {
      // Group all selected laws by category for visual display
      const groups = [];
      const leading = this.leadingCase;
      
      for (const category of this.categories) {
        const laws = this.selectedLawsDetails
          .filter(law => law.shortener === category.shortener);
        
        if (laws.length > 0) {
          groups.push({
            shortener: category.shortener,
            laws: laws.map(law => ({
              paragraph: law.paragraph,
              fine: law.fine,
              wanteds: law.wanteds,
              law: law.law,
              isLeading: leading && law.id === leading.id
            }))
          });
        }
      }
      
      return groups;
    },
    needsFib() {
      return this.selectedLawsDetails.some(law => law.fib);
    },
    needsSta() {
      return this.selectedLawsDetails.some(law => law.sta);
    },
    needsFineSplitting() {
      return this.totalFine > this.maxEntryFine;
    },
    numFineSplits() {
      if (!this.needsFineSplitting) return 1;
      return Math.ceil(this.totalFine / this.maxEntryFine);
    }
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    lawMatchesSearch(law) {
      if (!this.searchQuery.trim()) return true;
      
      const query = this.searchQuery.toLowerCase();
      return (
        law.paragraph.toLowerCase().includes(query) ||
        law.law.toLowerCase().includes(query) ||
        (law.info && law.info.toLowerCase().includes(query))
      );
    },
    filterLaws(laws) {
      if (!this.searchQuery.trim()) return laws;
      
      return laws.filter(law => this.lawMatchesSearch(law));
    },
    generatePdaFormat() {
      const day = new Date(this.prisonerData.day);
      const checkZero = this.checkZero;
      const currentTime = this.getCurrentTime();

      let pdaText = `${checkZero(day.getDate())}.${checkZero(day.getMonth() + 1)}.${day.getFullYear()} ${this.prisonerData.time} ${this.returnAllParagraphs}`;
      
      // Add license plate and location for StVO related offenses
      if (this.selectedLawsDetails.some(law => law.shortener === "StVO")) {
        pdaText += ` ${this.prisonerData.licensePlate ? '[' + this.prisonerData.licensePlate.toUpperCase() + ']' : "[N/A]"}`;
        
        if (this.prisonerData.location) {
          pdaText += ` ${this.prisonerData.location}`;
        }
      }
      
      // Add PDA notes if provided
      if (this.prisonerData.pdaNotes) {
        pdaText += ` (${this.prisonerData.pdaNotes})`;
      }
      
      // Add issue time if enabled
      if (this.addIssueTime) {
        pdaText += ` | Ausgestellt: ${currentTime}`;
      }
      
      // Add fine splitting notification if needed
      if (this.needsFineSplitting) {
        pdaText += ` 1/${this.numFineSplits}`;
      }
      
      return pdaText;
    },
    generateFineSplits() {
  if (!this.needsFineSplitting) return [];
  
  const splits = [];
  const totalFine = this.totalFine;
  const numSplits = this.numFineSplits;
  
  // First payment is always the max
  let firstAmount = Math.min(this.maxEntryFine, totalFine);
  splits.push({
    text: this.generatePdaFormat(),
    amount: firstAmount
  });
  
  // Generate additional payments
  for (let i = 1; i < numSplits; i++) {
    const day = new Date(this.prisonerData.day);
    const checkZero = this.checkZero;
    const currentTime = this.getCurrentTime();
    
    let remainingAmount = totalFine - (this.maxEntryFine * i);
    let paymentAmount = Math.min(remainingAmount, this.maxEntryFine);

    let pdaText = `${checkZero(day.getDate())}.${checkZero(day.getMonth() + 1)}.${day.getFullYear()} ${this.prisonerData.time} ${this.returnAllParagraphs}`;
    
    // Add license plate for StVO offenses
    if (this.selectedLawsDetails.some(law => law.shortener === "StVO")) {
      pdaText += ` ${this.prisonerData.licensePlate ? '[' + this.prisonerData.licensePlate.toUpperCase() + ']' : "[N/A]"}`;
      
      if (this.prisonerData.location) {
        pdaText += ` ${this.prisonerData.location}`;
      }
    }
    
    // Add PDA notes if provided
    if (this.prisonerData.pdaNotes) {
      pdaText += ` (${this.prisonerData.pdaNotes})`;
    }
    
    // Add issue time if enabled
    if (this.addIssueTime) {
      pdaText += ` | Ausgestellt: ${currentTime}`;
    }
    
    pdaText += ` ${i+1}/${numSplits}`;
    
    splits.push({
      text: pdaText,
      amount: paymentAmount
    });
  }
  
  return splits;
},
    generatePdaWantedFormat() {
      const day = new Date(this.prisonerData.day);
      const checkZero = this.checkZero;
      const currentTime = this.getCurrentTime();
      
      let pdaText = `${this.returnAllParagraphsWanteds} | ${checkZero(day.getDate())}.${checkZero(day.getMonth() + 1)}.${day.getFullYear()} - ${this.prisonerData.time}`;
      
      // Add PDA notes if provided
      if (this.prisonerData.pdaNotes) {
        pdaText += ` (${this.prisonerData.pdaNotes})`;
      }
      
      // If ersatzfreiheitsstrafe is active, add prefix
      if (this.ersatzfreiheitsstrafe) {
        pdaText = `Ersatzfreiheitsstrafe ${pdaText}`;
      }
      
      // Add issue time if enabled
      if (this.addIssueTime) {
        pdaText += ` | Ausgestellt: ${currentTime}`;
      }
      
      return pdaText;
    },
    getCurrentTime() {
      const now = new Date();
      return `${this.checkZero(now.getHours())}:${this.checkZero(now.getMinutes())}`;
    },
    selectLaw(id) {
      // Check for exclusions before adding
      const lawToAdd = this.findLawById(id);
      
      if (!lawToAdd) return;
      
      // If law is already selected, remove it
      if (this.selectedLaws.includes(id)) {
        this.removeLaw(id);
        return;
      }
      
      // Check for exclusions
      if (lawToAdd.exclusion_info) {
        // Parse exclusion info and check if any excluded laws are already selected
        const exclusions = lawToAdd.exclusion_info.split(/,\s*|§§|\s+/g)
          .filter(ex => ex && ex !== "StGB" && ex !== "WaffG" && ex !== "BtmG" && ex !== "StVO");
          
        // Remove any laws that would be excluded by this one
        const lawsToRemove = this.selectedLawsDetails.filter(law => {
          return exclusions.some(ex => law.paragraph.includes(ex));
        });
        
        lawsToRemove.forEach(law => this.removeLaw(law.id));
      }
      
      // Add the new law
      this.selectedLaws.push(id);
      
      // Sort laws by ID for consistent display
      this.selectedLaws.sort((a, b) => parseInt(a) - parseInt(b));
      
      // Start arrest countdown if there are wanteds
      if (this.totalWanteds > 0) {
        this.countTimeForArrest();
      }
    },
    findLawById(id) {
      for (const category of this.lawData.gesetze) {
        const law = category.paragraphs.find(p => p.id === id);
        if (law) return {...law, shortener: category.shortener};
      }
      return null;
    },
    removeLaw(id) {
      this.selectedLaws = this.selectedLaws.filter(lawId => lawId !== id);
    },
    clearSelection() {
      this.selectedLaws = [];
      if (this.arrestCountdown) {
        clearInterval(this.arrestCountdown);
        this.arrestTime = "";
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(value);
    },
    setTimeAndDateNow() {
      const now = new Date();
      this.prisonerData.day = `${now.getFullYear()}-${this.checkZero(now.getMonth() + 1)}-${this.checkZero(now.getDate())}`;
      this.prisonerData.time = `${this.checkZero(now.getHours())}:${this.checkZero(now.getMinutes())}`;
      
      if (this.totalWanteds > 0) {
        this.countTimeForArrest();
      }
    },
    countTimeForArrest() {
      // Clear any existing countdown
      if (this.arrestCountdown) {
        clearInterval(this.arrestCountdown);
      }
      
      if (!this.prisonerData.time || this.totalWanteds <= 0) return;
      
      const checkZero = this.checkZero;
      
      // Get arrest time (current time + 25 minutes)
      const day = new Date(this.prisonerData.day || new Date());
      const timeComponents = this.prisonerData.time.split(':');
      const hours = parseInt(timeComponents[0]);
      const minutes = parseInt(timeComponents[1]);
      
      day.setHours(hours, minutes, 0);
      
      // Add 25 minutes for arrest
      const countDownDate = day.getTime() + 25 * 60 * 1000;
      
      // Update countdown every second
      this.arrestCountdown = setInterval(() => {
        const now = new Date().getTime();
        const distance = countDownDate - now;
        
        // Calculate time components
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        // Display the countdown
        this.arrestTime = `${checkZero(minutes)}:${checkZero(seconds)}`;
        
        // If countdown is finished
        if (distance < 0) {
          clearInterval(this.arrestCountdown);
          this.arrestTime = "Abgelaufen";
        }
      }, 1000);
    },
    checkZero(num) {
      return num < 10 ? "0" + num : num;
    },
    async copyClipboard(val) {
  // For PDA texts, always generate them fresh to get up-to-date time
  if (typeof val === 'object' && val.text) {
    val = val.text;
  }
  
  if (typeof val === 'string' && val.includes('|')) {
    // Try to detect if this is a PDA text and update if needed
    if (val.includes(this.returnAllParagraphs)) {
      val = this.generatePdaFormat();
    } else if (val.includes(this.returnAllParagraphsWanteds)) {
      val = this.generatePdaWantedFormat();
    }
  }
  
  try {
    // Modern approach
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(val);
      return;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = val;
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Copy to clipboard failed:", err);
    }
    
    document.body.removeChild(textArea);
  } catch (e) {
    console.error("Copy to clipboard failed:", e);
  }
},
    newAct() {
      // Reset all data
      this.prisonerData = {
        name: "",
        licensePlate: "",
        location: "",
        day: "",
        time: "",
        pdaNotes: ""
      };
      
      this.selectedLaws = [];
      this.wantedReduce = "0";
      this.ersatzfreiheitsstrafe = false;
      this.activeTab = 'details';
      
      if (this.arrestCountdown) {
        clearInterval(this.arrestCountdown);
        this.arrestTime = "";
      }
      
      // Set current time
      this.setTimeAndDateNow();
    },
    saveRecord() {
      // Validate required fields
      if (!this.prisonerData.name || !this.prisonerData.time || this.selectedLaws.length === 0) {
        alert('Bitte füllen Sie alle erforderlichen Felder aus');
        return;
      }

      // console.log('Record saved:', {
      //   prisoner: this.prisonerData,
      //   laws: this.selectedLawsDetails,
      //   totalFine: this.totalFine,
      //   totalWanteds: this.totalWanteds,
      //   ersatzfreiheitsstrafe: this.ersatzfreiheitsstrafe,
      //   fineSplits: this.needsFineSplitting ? this.generateFineSplits() : [],
      //   pdaText: this.generatePdaFormat()
      // });
      
      this.newAct();
    }
  },
  mounted() {
    // Initialize with current time
    this.setTimeAndDateNow();

      fetch('https://lawapi-grpde03.statescloud.de/api/v2/data')
        .then(response => response.json())
        .then(data => {
          this.lawData = data;
        })
        .catch(error => {
          console.error('Error loading law data:', error);
        });
    
    this.socket = io('https://lightbox.state.rs');
    
    // Listen for visitor updates
    this.socket.on('visitorUpdate', (count) => {
      this.visitorCount = count;
    });       
    // Set up event listeners and other initialization
    window.addEventListener('beforeunload', () => {
      // Clean up timers
      if (this.arrestCountdown) {
        clearInterval(this.arrestCountdown);
      }
    });
  },
  beforeDestroy() {
    // Clean up socket connection when component is destroyed
    if (this.socket) {
      this.socket.disconnect();
    }
  },
  beforeUnmount() {
    // Clean up timers
    if (this.arrestCountdown) {
      clearInterval(this.arrestCountdown);
    }
    
    // Remove event listeners
    window.removeEventListener('beforeunload', () => {});
  }
}
</script>

<style>
/* Custom styles */
.text-amber-400 {
  color: #fbbf24;
}

input[type="time"].text-amber-400 {
  color: #fbbf24;
  font-weight: 500;
}

/* Custom styled scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 8px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

/* Firefox scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
}

/* Custom animation for copying */
@keyframes copy-pulse {
  0% { background-color: rgba(79, 70, 229, 0.2); }
  50% { background-color: rgba(79, 70, 229, 0.4); }
  100% { background-color: rgba(79, 70, 229, 0.2); }
}

.copy-active {
  animation: copy-pulse 0.5s ease-in-out;
}

/* FIB and STA badges */
.bg-blue-500\/30 {
  background-color: rgba(59, 130, 246, 0.3);
}

.bg-purple-500\/30 {
  background-color: rgba(168, 85, 247, 0.3);
}

/* Stronger colors for the authority requirement badges */
.bg-blue-600 {
  background-color: #2563eb;
}

.bg-purple-600 {
  background-color: #9333ea;
}

.bg-green-600 {
  background-color: #16a34a;
}

.bg-yellow-600\/50 {
  background-color: rgba(202, 138, 4, 0.5);
}

/* Gradient background for combined FIB & STA */
.bg-gradient-to-r.from-blue-600.to-purple-600 {
  background-image: linear-gradient(to right, #2563eb, #9333ea);
}

/* Make text areas with timestamps more readable */
.bg-black\/30 {
  background-color: rgba(0, 0, 0, 0.3);
}

.bg-black\/40:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Fix for sticky positioning on smaller screens */
@media (max-width: 1023px) {
  .lg\:sticky {
    position: relative;
    top: 0;
  }
}
body {
       background: linear-gradient(to bottom right, #001f58, #420000);
       min-height: 100vh;
   }
</style>


